import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getSeguimientosClientes,
  resolverSeguimientoCliente,
  deleteSeguimientoCliente,
} from "../helpers/rutaSeguimientosClientes";

import logo from "../images/ridersmiami-logo.png";

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();

const normalizarTexto = (value) => String(value || "").toUpperCase();

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const fechaISO = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR");
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const nombreClienteCompra = (cliente) => {
  if (!cliente) return "";

  if (typeof cliente === "string") return "";

  return (
    cliente.razonsocial ||
    cliente.nombreCompleto ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "";

  if (typeof usuario === "string") return "";

  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    ""
  );
};

const nroVenta = (venta) => {
  if (!venta) return "";

  if (typeof venta === "string") return "";

  return venta.nroventa || venta.nroVenta || venta.numero || venta.codventa || "";
};

const estadoSeguimientoBadge = (estado) => {
  const e = clean(estado).toUpperCase();

  if (e === "RESUELTO") return "badge bg-success";
  if (e === "EN_REVISION") return "badge bg-warning text-dark";
  if (e === "PENDIENTE") return "badge bg-danger";

  return "badge bg-secondary";
};

const prioridadSeguimientoBadge = (prioridad) => {
  const p = clean(prioridad).toUpperCase();

  if (p === "ALTA") return "badge bg-danger";

  return "badge bg-secondary";
};

export default function ReporteSeguimientosClientes() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());

  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [prioridad, setPrioridad] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filas, setFilas] = useState([]);

  const cargarReporte = async () => {
    setLoading(true);
    setError("");

    try {
      const LIMIT = 200;
      let page = 1;
      let total = 0;
      let seguimientos = [];

      do {
        const resp = await getSeguimientosClientes({
          page,
          limit: LIMIT,
          activo: "true",
          alcanceReporte: true,
        });

        const lote = Array.isArray(resp?.seguimientos)
          ? resp.seguimientos
          : [];

        seguimientos = seguimientos.concat(lote);
        total = Number(resp?.total || lote.length || 0);
        page += 1;
      } while (seguimientos.length < total);

      const filasArmadas = seguimientos.map((s) => {
        const cliente = s.clienteCompra || {};
        const venta = s.venta || {};
        const usuario = s.usuario || {};

        return {
          seguimientoId: s._id || s.id,
          codseg: s.codseg || "",
          fecha: s.createdAt || s.fecha || "",
          fechaISO: fechaISO(s.createdAt || s.fecha),
          cliente,
          clienteNombre: nombreClienteCompra(cliente),
          venta,
          ventaId: getId(venta),
          nroventa: nroVenta(venta),
          estado: s.estado || "PENDIENTE",
          prioridad: s.prioridad || "NORMAL",
          texto: s.texto || "",
          usuario,
          usuarioNombre: nombreUsuario(usuario),
        };
      });

      setFilas(filasArmadas);
    } catch (err) {
      console.error("Error reporte seguimientos:", err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar el reporte de seguimientos"
      );

      setFilas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filasFiltradas = useMemo(() => {
    const texto = clean(q).toUpperCase();

    return filas.filter((fila) => {
      if (desde && fila.fechaISO && fila.fechaISO < desde) return false;
      if (hasta && fila.fechaISO && fila.fechaISO > hasta) return false;

      if (estado && fila.estado !== estado) return false;
      if (prioridad && fila.prioridad !== prioridad) return false;

      if (texto) {
        const cadena = [
          fila.codseg,
          fila.nroventa,
          fila.clienteNombre,
          fila.estado,
          fila.prioridad,
          fila.texto,
          fila.usuarioNombre,
        ]
          .join(" ")
          .toUpperCase();

        if (!cadena.includes(texto)) return false;
      }

      return true;
    });
  }, [filas, desde, hasta, estado, prioridad, q]);

  const totales = useMemo(() => {
    const total = filasFiltradas.length;

    const pendientes = filasFiltradas.filter(
      (f) => f.estado === "PENDIENTE"
    ).length;

    const enRevision = filasFiltradas.filter(
      (f) => f.estado === "EN_REVISION"
    ).length;

    const resueltos = filasFiltradas.filter(
      (f) => f.estado === "RESUELTO"
    ).length;

    const alta = filasFiltradas.filter((f) => f.prioridad === "ALTA").length;

    return {
      total,
      pendientes,
      enRevision,
      resueltos,
      alta,
    };
  }, [filasFiltradas]);

  const limpiarFiltros = () => {
    setDesde(firstDayMonthISO());
    setHasta(todayLocalISO());
    setQ("");
    setEstado("");
    setPrioridad("");
  };

  const resolver = async (fila) => {
    if (!fila?.seguimientoId) return;

    if (!window.confirm("¿Marcar este seguimiento como RESUELTO?")) return;

    try {
      await resolverSeguimientoCliente(fila.seguimientoId);
      await cargarReporte();
    } catch (err) {
      console.error("Error resolviendo seguimiento:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo resolver el seguimiento."
      );
    }
  };

  const eliminar = async (fila) => {
    if (!fila?.seguimientoId) return;

    if (!window.confirm("¿Eliminar este seguimiento?")) return;

    try {
      await deleteSeguimientoCliente(fila.seguimientoId);
      await cargarReporte();
    } catch (err) {
      console.error("Error eliminando seguimiento:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo eliminar el seguimiento."
      );
    }
  };

  const generarPDF = async () => {
    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      const logoImg = await cargarImagen(logo);

      doc.addImage(logoImg, "PNG", 10, 8, 34, 12);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Reporte de Seguimientos de Clientes", 50, 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Riders Miami", 50, 20);

      doc.text(`Desde: ${desde || "-"}   Hasta: ${hasta || "-"}`, 287, 14, {
        align: "right",
      });

      doc.text(
        `Estado: ${estado || "Todos"} | Prioridad: ${prioridad || "Todas"}`,
        287,
        20,
        { align: "right" }
      );

      doc.setDrawColor(180);
      doc.line(10, 24, 287, 24);

      const resumenY = 28;
      const boxW = 36;
      const boxH = 14;
      const gap = 3;

      const resumen = [
        { label: "Total", value: String(totales.total || 0) },
        { label: "Pendientes", value: String(totales.pendientes || 0) },
        { label: "En revisión", value: String(totales.enRevision || 0) },
        { label: "Alta prioridad", value: String(totales.alta || 0) },
        { label: "Resueltos", value: String(totales.resueltos || 0) },
      ];

      resumen.forEach((item, i) => {
        const x = 10 + i * (boxW + gap);

        doc.setDrawColor(210);
        doc.roundedRect(x, resumenY, boxW, boxH, 1.5, 1.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.text(item.label, x + 1.5, resumenY + 4.5);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(String(item.value), x + 1.5, resumenY + 10);
      });

      const body = filasFiltradas.map((fila) => [
        formatDate(fila.fecha),
        fila.nroventa || "-",
        normalizarTexto(fila.clienteNombre),
        fila.estado || "",
        fila.prioridad || "",
        normalizarTexto(fila.texto),
        normalizarTexto(fila.usuarioNombre),
      ]);

      autoTable(doc, {
        startY: 48,
        theme: "grid",
        margin: { left: 6, right: 6 },
        head: [
          [
            "Fecha",
            "Venta",
            "Cliente",
            "Estado",
            "Prioridad",
            "Detalle",
            "Usuario",
          ],
        ],
        body,
        styles: {
          fontSize: 6.5,
          cellPadding: 1.2,
          overflow: "linebreak",
          valign: "middle",
          minCellHeight: 5,
        },
        headStyles: {
          fillColor: [33, 37, 41],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 6.5,
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 18, halign: "center" },
          1: { cellWidth: 12, halign: "center" },
          2: { cellWidth: 48 },
          3: { cellWidth: 24, halign: "center" },
          4: { cellWidth: 22, halign: "center" },
          5: { cellWidth: 120 },
          6: { cellWidth: 45 },
        },
        didParseCell: (data) => {
          if (data.section !== "body") return;

          if (data.column.index === 3) {
            const value = String(data.cell.raw || "").toUpperCase();

            if (value === "PENDIENTE") {
              data.cell.styles.textColor = [220, 53, 69];
              data.cell.styles.fontStyle = "bold";
            }

            if (value === "EN_REVISION") {
              data.cell.styles.textColor = [180, 120, 0];
              data.cell.styles.fontStyle = "bold";
            }

            if (value === "RESUELTO") {
              data.cell.styles.textColor = [25, 135, 84];
              data.cell.styles.fontStyle = "bold";
            }
          }

          if (data.column.index === 4) {
            const value = String(data.cell.raw || "").toUpperCase();

            if (value === "ALTA") {
              data.cell.styles.textColor = [220, 53, 69];
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
      });

      doc.save(
        `reporte-seguimientos-${desde || "desde"}-${hasta || "hasta"}.pdf`
      );
    } catch (error) {
      console.error("Error generando PDF:", error);
      alert("No se pudo generar el PDF.");
    }
  };

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">Reporte de Seguimientos</h4>
              <small className="text-muted">Riders Miami</small>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={limpiarFiltros}
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={cargarReporte}
                disabled={loading}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={loading || filasFiltradas.length === 0}
              >
                PDF
              </Button>
            </div>
          </div>

          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} sm={6} lg>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                size="sm"
              >
                <option value="">Todos</option>
                <option value="PENDIENTE">PENDIENTE</option>
                <option value="EN_REVISION">EN REVISIÓN</option>
                <option value="RESUELTO">RESUELTO</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Prioridad</Form.Label>
              <Form.Select
                value={prioridad}
                onChange={(e) => setPrioridad(e.target.value)}
                size="sm"
              >
                <option value="">Todas</option>
                <option value="NORMAL">NORMAL</option>
                <option value="ALTA">ALTA</option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>Buscar</Form.Label>
              <Form.Control
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cliente, venta, detalle..."
                size="sm"
              />
            </Col>
          </Row>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          <Row className="g-2 mb-3">
            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Total</small>
                  <h5 className="mb-0">{totales.total}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Pendientes</small>
                  <h5 className="mb-0 text-danger">{totales.pendientes}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">En revisión</small>
                  <h5 className="mb-0 text-warning">{totales.enRevision}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Alta prioridad</small>
                  <h5 className="mb-0 text-danger">{totales.alta}</h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <small className="text-muted">Resueltos</small>
                  <h5 className="mb-0 text-success">{totales.resueltos}</h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle reporte-seguimientos-table"
            >
              <thead
                className="table-dark"
                style={{ position: "sticky", top: 0, zIndex: 2 }}
              >
                <tr>
                  <th>Fecha</th>
                  <th>Venta</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Prioridad</th>
                  <th>Detalle</th>
                  <th>Usuario</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Cargando reporte...
                    </td>
                  </tr>
                ) : filasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No hay seguimientos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filasFiltradas.map((fila, index) => (
                    <tr key={`${fila.seguimientoId}-${index}`}>
                      <td>{formatDate(fila.fecha)}</td>

                      <td className="fw-bold">
                        {fila.nroventa ? `N° ${fila.nroventa}` : "-"}
                      </td>

                      <td>{normalizarTexto(fila.clienteNombre)}</td>

                      <td>
                        <span className={estadoSeguimientoBadge(fila.estado)}>
                          {String(fila.estado || "").replace("_", " ")}
                        </span>
                      </td>

                      <td>
                        <span
                          className={prioridadSeguimientoBadge(fila.prioridad)}
                        >
                          {fila.prioridad}
                        </span>
                      </td>

                      <td style={{ minWidth: 320 }}>
                        {normalizarTexto(fila.texto)}
                      </td>

                      <td>{normalizarTexto(fila.usuarioNombre)}</td>

                      <td className="text-center">
                        {fila.estado !== "RESUELTO" && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            onClick={() => resolver(fila)}
                            disabled={loading}
                            title="Marcar resuelto"
                          >
                            ✓
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => eliminar(fila)}
                          disabled={loading}
                          title="Eliminar"
                        >
                          🗑
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {filasFiltradas.length > 0 && (
                <tfoot>
                  <tr>
                    <th colSpan="3">Totales</th>
                    <th>Pend.: {totales.pendientes}</th>
                    <th>Alta: {totales.alta}</th>
                    <th colSpan="3">Total seguimientos: {totales.total}</th>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}